import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { hasZendeskSession } from 'components/ZendeskChat';
import {
	useGlobalEvent,
	useRequiredContext,
	useValueChangeEffect,
} from 'hooks';
import { sendGlobalEvent } from 'utils/helpers';

interface ChatContextType {
	activateChatWidget: () => void;
	/** If the chat widget should be loaded at all. */
	chatWidgetIsActive: boolean;
	/** If the chat widget is loaded and ready to be interacted with. */
	chatWidgetIsReady: boolean;
	messageCount: number;
}
const ChatContext = createContext<ChatContextType | undefined>(undefined);
ChatContext.displayName = 'ChatContext';

export function ChatContextProvider({ children }: { children: ReactNode }) {
	const [messageCount, setMessageCount] = useState(0);
	const [chatWidgetIsActive, setChatWidgetIsActive] =
		useState(hasZendeskSession());
	const [chatWidgetIsReady, setChatWidgetIsReady] = useState(false);

	useGlobalEvent('chat-widget-unread-count', ({ detail }) => {
		setMessageCount(detail.count);
	});

	useGlobalEvent('chat-widget-init', () => {
		setChatWidgetIsReady(true);
	});

	// Separate from the init event and triggered in an effect to ensure the
	// chatWidgetIsReady state is set when listeners run.
	useValueChangeEffect(chatWidgetIsReady, () => {
		if (chatWidgetIsReady) {
			sendGlobalEvent('chat-widget-ready');
		}
	});

	useGlobalEvent('chat-widget-reset', () => {
		setMessageCount(0);
		setChatWidgetIsActive(false);
		setChatWidgetIsReady(false);
	});

	const activateChatWidget = useCallback(() => {
		setChatWidgetIsActive(true);
	}, []);

	const value = useMemo(
		() => ({
			chatWidgetIsActive,
			chatWidgetIsReady,
			messageCount,
			activateChatWidget,
		}),
		[activateChatWidget, chatWidgetIsActive, chatWidgetIsReady, messageCount],
	);

	return <ChatContext value={value}>{children}</ChatContext>;
}
ChatContextProvider.displayName = 'ChatContextProvider';

export function useChatContext() {
	return useRequiredContext(ChatContext, 'ChatContext');
}
